import ScheduleAlerts from "./ScheduleAlerts";
import { sendToDispatchWithPrompt } from "./CardReadiness";
import React, { useEffect, useMemo, useState } from "react";
import SearchBar from "@/components/SearchBar";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, CalendarDays, Search, Inbox, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import MobPopover from "./MobPopover";
import { smartTitleFromMob } from "../lib/mobTitle";

// ── date helpers ─────────────────────────────────────────────────────────────
const d2 = (n) => String(n).padStart(2, "0");
const fmtIso = (d) => `${d.getFullYear()}-${d2(d.getMonth() + 1)}-${d2(d.getDate())}`;
const parseIso = (s) => { const [y, m, day] = (s || "").split("-").map(Number); return new Date(y, m - 1, day); };
const addDays = (d, n) => { const x = new Date(d); x.setDate(d.getDate() + n); return x; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
// Monday-first: Sunday(0) → 6, Monday(1) → 0, etc.
const monIdx = (d) => (d.getDay() + 6) % 7;
const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
const isWeekendIso = (iso) => isWeekend(parseIso(iso));
// Add N working days (Mon–Fri) to an ISO date — rolls over weekends.
const addWorkingDays = (isoStart, workingDays) => {
  const cur = parseIso(isoStart);
  let added = 1; // start day counts as day 1
  while (added < workingDays) {
    cur.setDate(cur.getDate() + 1);
    if (!isWeekend(cur)) added += 1;
  }
  return fmtIso(cur);
};
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Deterministic color per project id (chip background).
const PROJECT_COLORS = [
  { bg: "bg-blue-100 dark:bg-blue-950/50",       text: "text-blue-800 dark:text-blue-200",       border: "border-blue-200 dark:border-blue-900" },
  { bg: "bg-emerald-100 dark:bg-emerald-950/50", text: "text-emerald-800 dark:text-emerald-200", border: "border-emerald-200 dark:border-emerald-900" },
  { bg: "bg-purple-100 dark:bg-purple-950/50",   text: "text-purple-800 dark:text-purple-200",   border: "border-purple-200 dark:border-purple-900" },
  { bg: "bg-amber-100 dark:bg-amber-950/50",     text: "text-amber-800 dark:text-amber-200",     border: "border-amber-200 dark:border-amber-900" },
  { bg: "bg-rose-100 dark:bg-rose-950/50",       text: "text-rose-800 dark:text-rose-200",       border: "border-rose-200 dark:border-rose-900" },
  { bg: "bg-cyan-100 dark:bg-cyan-950/50",       text: "text-cyan-800 dark:text-cyan-200",       border: "border-cyan-200 dark:border-cyan-900" },
  { bg: "bg-indigo-100 dark:bg-indigo-950/50",   text: "text-indigo-800 dark:text-indigo-200",   border: "border-indigo-200 dark:border-indigo-900" },
  { bg: "bg-teal-100 dark:bg-teal-950/50",       text: "text-teal-800 dark:text-teal-200",       border: "border-teal-200 dark:border-teal-900" },
];
const hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};
const colorFor = (projectId) => PROJECT_COLORS[hashStr(projectId || "") % PROJECT_COLORS.length];

// A mob spans [start, end] using scheduled dates if present, else requested dates.
// Weekend days that are neither the start nor the end are excluded from the render
// so the calendar visually reflects that crews don't work Sat/Sun by default.
// Returns { days: [iso, …], requestedOnly: bool }.
const mobDays = (m) => {
  const scheduled = !!m.scheduled_start_date;
  const start = m.scheduled_start_date || m.requested_start_date;
  if (!start) return { days: [], requestedOnly: false };
  const end = (scheduled ? m.scheduled_end_date : m.requested_end_date) || start;
  const s = parseIso(start);
  const e = parseIso(end);
  const span = Math.max(1, Math.round((e - s) / 86400000) + 1);
  const days = [];
  for (let i = 0; i < span; i++) {
    const iso = fmtIso(addDays(s, i));
    // Skip mid-span weekends. Always keep start & end so manual weekend schedules show.
    if (isWeekendIso(iso) && iso !== start && iso !== end) continue;
    days.push(iso);
  }
  return { days, requestedOnly: !scheduled };
};

export default function ScheduleCalendar() {
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [mobs, setMobs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [popoverMob, setPopoverMob] = useState(null);
  const [selected, setSelected] = useState([]);   // SCH-1 §9 multi-select (shift/ctrl-click pills) → one Send
  const [poolOpen, setPoolOpen] = useState(true);
  const [dragMobId, setDragMobId] = useState(null);
  const [dropTargetIso, setDropTargetIso] = useState(null);
  const [poolDragOver, setPoolDragOver] = useState(false);

  // Load a slightly-padded window so multi-day mobs that straddle the month edges render fully.
  const rangeFrom = fmtIso(addDays(startOfMonth(cursor), -14));
  const rangeTo = fmtIso(addDays(endOfMonth(cursor), 14));

  const load = async () => {
    setLoading(true);
    try {
      const [m, p, s] = await Promise.all([
        api.get("/mobilizations"),
        api.get("/projects"),
        api.get("/scope-codes"),
      ]);
      setMobs(m.data || []);
      setProjects(p.data || []);
      setScopes(s.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const projectsMap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  const scopeMap = useMemo(() => {
    const m = {};
    for (const s of scopes) if (s.trade) m[s.trade.toLowerCase()] = s.code;
    return m;
  }, [scopes]);

  // Search filter — matches project name / job # / scope title.
  const filteredMobs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return mobs;
    return mobs.filter((m) => {
      const p = projectsMap[m.project_id] || {};
      const scope = (smartTitleFromMob(m, scopeMap) || {}).title || "";
      return (
        (p.project_name || "").toLowerCase().includes(q) ||
        (p.job_number || "").toLowerCase().includes(q) ||
        scope.toLowerCase().includes(q)
      );
    });
  }, [mobs, search, projectsMap, scopeMap]);

  // date iso → [{mob, requestedOnly}, ...]
  const mobsByDay = useMemo(() => {
    const map = new Map();
    for (const m of filteredMobs) {
      const { days, requestedOnly } = mobDays(m);
      for (const iso of days) {
        if (!map.has(iso)) map.set(iso, []);
        map.get(iso).push({ mob: m, requestedOnly });
      }
    }
    return map;
  }, [filteredMobs]);

  // Build 6-row × 7-col grid starting on the Monday on/before day 1.
  const cells = useMemo(() => {
    const first = startOfMonth(cursor);
    const gridStart = addDays(first, -monIdx(first));
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [cursor]);

  const monthCounts = useMemo(() => {
    const first = fmtIso(startOfMonth(cursor));
    const last = fmtIso(endOfMonth(cursor));
    const inMonthMobIds = new Set();
    const inMonthProjectIds = new Set();
    for (const [iso, entries] of mobsByDay.entries()) {
      if (iso >= first && iso <= last) {
        for (const { mob } of entries) { inMonthMobIds.add(mob.id); inMonthProjectIds.add(mob.project_id); }
      }
    }
    return { mobs: inMonthMobIds.size, projects: inMonthProjectIds.size };
  }, [mobsByDay, cursor]);

  // Schedule Alerts: every mob that isn't firmly scheduled yet — including those
  // with only a requested date (which also render as dashed chips on the calendar).
  const poolMobs = useMemo(
    () => mobs.filter((m) => !m.scheduled_start_date),
    [mobs],
  );

  // Search-filter helper for pool cards.
  const searchMatches = (m) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const p = projectsMap[m.project_id] || {};
    const scope = (smartTitleFromMob(m, scopeMap) || {}).title || "";
    return (
      (p.project_name || "").toLowerCase().includes(q) ||
      (p.job_number || "").toLowerCase().includes(q) ||
      scope.toLowerCase().includes(q)
    );
  };

  // Drop a pool mob (or any dragged mob) on a calendar day → schedule for that day.
  // Weekend drops require explicit confirmation. Multi-day spans skip Sat/Sun.
  const scheduleMobOnDay = async (mobId, iso) => {
    const m = mobs.find((x) => x.id === mobId);
    if (!m) return;
    if (isWeekendIso(iso)) {
      const dayName = parseIso(iso).toLocaleDateString(undefined, { weekday: "long" });
      const ok = window.confirm(
        `${dayName} (${iso}) is a weekend. Weekends are skipped by default.\n\nSchedule on this day anyway?`,
      );
      if (!ok) return;
    }
    const workingDays = Math.max(1, Math.ceil(((Number(m.estimated_duration_hours) || 8) / Math.max(1, Number(m.num_installers) || 1)) / 10));
    const endDate = addWorkingDays(iso, workingDays);
    try {
      await api.patch(`/mobilizations/${mobId}`, {
        scheduled_start_date: iso,
        scheduled_end_date: endDate,
        status: m.status === "requested" ? "scheduled" : m.status,
      });
      const skipped = fmtIso(addDays(parseIso(iso), workingDays - 1)) !== endDate;
      toast.success(
        `Scheduled for ${iso}${workingDays > 1 ? ` (${workingDays} working day${workingDays === 1 ? "" : "s"}${skipped ? ", weekends skipped" : ""})` : ""}`,
      );
      load();
    } catch {
      toast.error("Could not schedule");
    }
  };

  // Drop on the pool → unschedule (send whole mob back to the pool).
  const onPoolDrop = async (e) => {
    e.preventDefault();
    setPoolDragOver(false);
    if (!dragMobId) return;
    const m = mobs.find((x) => x.id === dragMobId);
    if (!m) return;
    try {
      await api.patch(`/mobilizations/${dragMobId}`, {
        scheduled_start_date: "",
        scheduled_end_date: "",
        requested_start_date: "",
        requested_end_date: "",
        status: "requested",
      });
      toast.success(`"${m.title || "mob"}" sent to Schedule Alerts`);
      load();
    } catch {
      toast.error("Could not move to pool");
    }
  };

  const gotoPrev = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const gotoNext = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  const gotoToday = () => setCursor(startOfMonth(new Date()));

  const monthLabel = `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`;

  return (
    <div className="flex gap-3 items-start" data-testid="schedule-calendar">
      {/* ── Schedule Alerts aside ─────────────────────────────────────────── */}
      <aside
        className={`${poolOpen ? "w-[260px]" : "w-[44px]"} shrink-0 border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 rounded-lg flex flex-col overflow-hidden transition-all self-stretch`}
      >
        {!poolOpen ? (
          <button
            onClick={() => setPoolOpen(true)}
            data-testid="cal-pool-open"
            className="p-2 flex flex-col items-center gap-2 h-full text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800"
            title="Open Schedule Alerts"
          >
            <Inbox className="w-4 h-4" />
            <span className="text-[10px] rotate-180 [writing-mode:vertical-rl]">Schedule Alerts</span>
          </button>
        ) : (
          <>
            <div className="flex justify-end px-2 pt-1">
              <button onClick={() => setPoolOpen(false)} data-testid="cal-pool-close" className="text-xs text-muted-foreground hover:text-foreground px-1.5">–</button>
            </div>
            <ScheduleAlerts mobsById={Object.fromEntries(mobs.map((m) => [m.id, m]))} onOpenMob={(m) => setPopoverMob(m)}
                            onDragMob={setDragMobId} onChanged={load} />
          </>
        )}
      </aside>

      {/* ── Calendar grid ───────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 space-y-3">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {selected.length > 0 && (
          <div className="flex items-center gap-2 text-xs" data-testid="cal-selection-bar">
            <span className="text-muted-foreground">{selected.length} selected</span>
            <button type="button" data-testid="cal-send-selected" onClick={() => sendToDispatchWithPrompt(selected, () => { setSelected([]); load(); })}
                    className="rounded border border-border bg-foreground text-background px-2 py-1 font-medium">Send {selected.length} to Dispatch →</button>
            <button type="button" onClick={() => setSelected([])} className="text-muted-foreground hover:text-foreground">clear</button>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={gotoPrev}
            data-testid="cal-prev"
            className="h-8 w-8 p-0"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={gotoToday}
            data-testid="cal-today"
            className="h-8"
          >
            Today
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={gotoNext}
            data-testid="cal-next"
            className="h-8 w-8 p-0"
            aria-label="Next month"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <h2 className="ml-2 text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-100" data-testid="cal-month-label">
            {monthLabel}
          </h2>
          <span className="hidden sm:inline text-xs text-gray-500 dark:text-gray-400 ml-1">
            · {monthCounts.projects} projects · {monthCounts.mobs} mobilizations
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-3 text-[11px] text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded border bg-blue-100 border-blue-200 dark:bg-blue-950/50 dark:border-blue-900" />
              Scheduled
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded border border-dashed bg-blue-100 border-blue-400 dark:bg-blue-950/50 dark:border-blue-500" />
              Requested
            </span>
          </div>
          <SearchBar value={search} onChange={setSearch}
                     placeholder="Search..." testId="cal-search" />
        </div>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-px rounded-md overflow-hidden border border-gray-200 dark:border-gray-800 bg-gray-200 dark:bg-gray-800">
        {DOW.map((d) => (
          <div
            key={d}
            className="bg-gray-50 dark:bg-gray-900 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-2 py-1.5 text-center"
          >
            {d}
          </div>
        ))}

        {/* Day cells */}
        {cells.map((d) => {
          const iso = fmtIso(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = fmtIso(today) === iso;
          const dow = monIdx(d);
          const isWknd = dow >= 5;
          const dayMobs = mobsByDay.get(iso) || [];
          const shown = dayMobs.slice(0, 4);
          const overflow = dayMobs.length - shown.length;
          const isDropTarget = dropTargetIso === iso;
          return (
            <div
              key={iso}
              data-testid={`cal-cell-${iso}`}
              onDragOver={(e) => {
                if (!dragMobId) return;
                e.preventDefault();
                if (dropTargetIso !== iso) setDropTargetIso(iso);
              }}
              onDragLeave={() => { if (dropTargetIso === iso) setDropTargetIso(null); }}
              onDrop={(e) => {
                e.preventDefault();
                setDropTargetIso(null);
                if (dragMobId) scheduleMobOnDay(dragMobId, iso);
              }}
              className={[
                "min-h-[112px] p-1.5 flex flex-col gap-1 text-left transition-colors",
                inMonth
                  ? (isWknd
                      ? "bg-gray-50 dark:bg-gray-900/60"
                      : "bg-white dark:bg-gray-950")
                  : "bg-gray-50/70 dark:bg-gray-900/40",
                isToday ? "ring-2 ring-inset ring-blue-500 dark:ring-blue-400" : "",
                // Weekend cells during an active drag get an amber "requires confirm" affordance.
                isDropTarget
                  ? (isWknd
                      ? "outline outline-2 outline-amber-500 bg-amber-50 dark:bg-amber-950/40"
                      : "outline outline-2 outline-blue-500 bg-blue-50 dark:bg-blue-950/40")
                  : "",
              ].join(" ")}
            >
              <div className="flex items-center justify-between">
                <span
                  className={[
                    "inline-flex items-center justify-center w-6 h-6 text-xs font-semibold rounded-full",
                    isToday
                      ? "bg-blue-600 text-white"
                      : inMonth
                        ? "text-gray-700 dark:text-gray-200"
                        : "text-gray-400 dark:text-gray-600",
                  ].join(" ")}
                >
                  {d.getDate()}
                </span>
                {dayMobs.length > 0 && (
                  <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500">
                    {dayMobs.length}
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-0.5 overflow-hidden">
                {shown.map(({ mob: m, requestedOnly }) => {
                  const p = projectsMap[m.project_id] || {};
                  const c = colorFor(m.project_id);
                  const scope = (smartTitleFromMob(m, scopeMap) || {}).title || "";
                  const title = p.project_name || "(No job)";
                  return (
                    <button
                      key={`${m.id}-${iso}`}
                      draggable
                      onDragStart={(e) => { e.stopPropagation(); setDragMobId(m.id); }}
                      onDragEnd={() => setDragMobId(null)}
                      onClick={(e) => { if (e.shiftKey || e.metaKey || e.ctrlKey) { e.preventDefault(); setSelected((cur) => cur.includes(m.id) ? cur.filter((x) => x !== m.id) : [...cur, m.id]); } else setPopoverMob(m); }}
                      data-selected={selected.includes(m.id) ? "1" : "0"}
                      data-testid={`cal-chip-${m.id}`}
                      title={`${title}${p.job_number ? ` · #${p.job_number}` : ""}${scope ? ` · ${scope}` : ""}${requestedOnly ? " · Requested (unscheduled)" : ""}`}
                      className={[
                        "text-left px-1.5 py-0.5 rounded text-[11px] leading-tight truncate cursor-grab active:cursor-grabbing",
                        selected.includes(m.id) ? "ring-2 ring-foreground" : "",
                        "hover:brightness-95 dark:hover:brightness-110 transition",
                        c.bg, c.text,
                        requestedOnly
                          ? "border border-dashed opacity-85 " + c.border
                          : "border " + c.border,
                      ].join(" ")}
                    >
                      <span className="font-semibold truncate">{title}</span>
                      {scope && (
                        <span className="opacity-75"> · {scope}</span>
                      )}
                    </button>
                  );
                })}
                {overflow > 0 && (
                  <span className="text-[10px] text-gray-500 dark:text-gray-400 pl-1">
                    + {overflow} more
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {loading && (
        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
          <CalendarDays className="w-3.5 h-3.5" /> Loading calendar…
        </div>
      )}

      {popoverMob && (
        <MobPopover
          open={!!popoverMob}
          onOpenChange={(o) => { if (!o) setPopoverMob(null); }}
          mob={popoverMob}
          project={projectsMap[popoverMob.project_id]}
          scopeCode={scopeMap[(popoverMob.scope_trade || "").toLowerCase()] || ""}
          onChanged={() => { setPopoverMob(null); load(); }}
          onDispatch={() => { setPopoverMob(null); load(); }}
          onDelete={() => { setPopoverMob(null); load(); }}
        />
      )}
      </div>
    </div>
  );
}

// ─── Pool card (draggable) ────────────────────────────────────────────────────
function PoolCard({ mob, project, scopeMap, onDragStart, onDragEnd, onClick }) {
  const { title: cardTitle, units: smartUnits } = smartTitleFromMob(mob, scopeMap || {});
  const hours = Math.round(Number(mob.estimated_duration_hours) || 8);
  const insts = mob.num_installers || 1;
  const reqDate = mob.requested_start_date || "";
  const reqDateShort = reqDate
    ? new Date(reqDate + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "";
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      data-testid={`cal-pool-card-${mob.id}`}
      className={`border rounded-md px-1.5 py-1 text-xs cursor-grab active:cursor-grabbing hover:shadow-sm transition ${
        mob.is_punchlist
          ? "bg-orange-50 border-orange-300 hover:border-orange-500 dark:bg-orange-950/40 dark:border-orange-800"
          : reqDate
            ? "bg-white border-amber-300 border-dashed hover:border-amber-500 dark:bg-gray-950 dark:border-amber-700"
            : "bg-white border-gray-200 hover:border-blue-400 dark:bg-gray-950 dark:border-gray-800 dark:hover:border-blue-500"
      }`}
    >
      <div className="flex items-center justify-between gap-1">
        <div className="font-semibold truncate flex-1 min-w-0 text-[12px]" title={project?.project_name || mob.title}>
          {project?.id ? (
            <Link
              to={`/projects/${project.id}`}
              onClick={(e) => e.stopPropagation()}
              className="text-blue-700 dark:text-blue-400 hover:underline"
              title="Open job"
              data-testid={`cal-pool-link-${mob.id}`}
            >
              {project.project_name || mob.title}
            </Link>
          ) : (
            <span className="text-gray-900 dark:text-gray-100">{mob.title}</span>
          )}
        </div>
        {reqDate ? (
          <span
            title={`Requested ${reqDate} — not yet firmly scheduled. Drop on a day to lock it in.`}
            data-testid={`cal-pool-reqdate-${mob.id}`}
            className="inline-flex items-center text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-800"
          >
            Req {reqDateShort}
          </span>
        ) : (
          <span
            title="No date — drag onto a day to schedule"
            className="inline-flex items-center text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-800"
          >
            <AlertTriangle className="w-2.5 h-2.5" />
          </span>
        )}
      </div>
      <div className="text-[10px] text-gray-600 dark:text-gray-400 truncate flex items-center gap-1.5">
        {project?.job_number && <span className="text-gray-500 dark:text-gray-500">{project.job_number}</span>}
        <span className="font-mono font-semibold text-gray-900 dark:text-gray-100">{cardTitle}</span>
        {smartUnits && <span className="text-gray-500 dark:text-gray-500 truncate">{smartUnits}</span>}
        <span className="ml-auto tabular-nums text-gray-700 dark:text-gray-300 shrink-0">{insts}I · {hours}h</span>
      </div>
    </div>
  );
}
